import { Layers, AlertCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ConsBusinessStats, ConsBusinessFilters, CbStatus } from '../../types/consolidatedBusiness';
import { CB_STATUS_META, CB_STATUS_ORDER } from './status';
import { fmtNum } from './format';

interface Props {
    stats: ConsBusinessStats | null;
    filters: ConsBusinessFilters;
    onChange: (f: ConsBusinessFilters) => void;
}

type TileKey = 'all' | 'overdue' | CbStatus;

interface Tile {
    key: TileKey;
    label: string;
    value: number | undefined;
    icon: LucideIcon;
    color: string;
}

/**
 * Six clickable KPI cards (D5). Each sets its filter; clicking the active one clears it.
 *
 * The NUMBERS never move when a card is clicked — the stats endpoint deletes `status` and
 * `onlyOverdue` from its own scope, so the tiles keep describing the population while the list
 * below describes the selection. "5 overdue of 2'400" is only an honest sentence if the 2'400
 * is everything.
 *
 * "Unchecked" was renamed "Open" so the card, the filter pill and the drawer speak the same
 * language: three names for one state is how a user learns to distrust the page.
 */
export function ConsBusinessStatsBar({ stats, filters, onChange }: Props) {
    const activeStatus = filters.status;
    const overdueActive = !!filters.onlyOverdue;
    // Hide-lost counts: with write-offs filtered out the page is NOT showing everything, so the
    // "Combinations" tile must not sit there highlighted claiming it is.
    const allActive = !activeStatus && !overdueActive && !filters.hideLost;

    const statusValue: Record<CbStatus, number | undefined> = {
        Open: stats?.uncheckedGroups,
        InProgress: stats?.inProgressGroups,
        Checked: stats?.checkedGroups,
        Lost: stats?.lostGroups,
    };

    const tiles: Tile[] = [
        { key: 'all', label: 'Combinations', value: stats?.totalGroups, icon: Layers, color: 'text-gray-600' },
        { key: 'overdue', label: 'Overdue', value: stats?.overdueGroups, icon: AlertCircle, color: 'text-red-600' },
        ...CB_STATUS_ORDER.map((s): Tile => ({
            key: s,
            label: CB_STATUS_META[s].label,
            value: statusValue[s],
            icon: CB_STATUS_META[s].icon,
            color: CB_STATUS_META[s].tileClass,
        })),
    ];

    const isActive = (key: TileKey) =>
        key === 'all' ? allActive : key === 'overdue' ? overdueActive : activeStatus === key;

    const toggle = (key: TileKey) => {
        if (key === 'all') {
            // "Combinations" is the reset: it clears the status, the overdue toggle AND hide-lost,
            // which is what "show me everything" means. Clearing only some leaves a filter the
            // user just told the page to drop — and hide-lost is the one that would be easiest to
            // forget, because it hides rows without naming them anywhere.
            onChange({ ...filters, status: undefined, onlyOverdue: false, hideLost: undefined });
            return;
        }
        if (key === 'overdue') {
            onChange({ ...filters, onlyOverdue: !overdueActive });
            return;
        }
        onChange({ ...filters, status: activeStatus === key ? undefined : key });
    };

    return (
        <>
            {tiles.map(t => {
                const Icon = t.icon;
                const active = isActive(t.key);
                return (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => toggle(t.key)}
                        aria-pressed={active}
                        className={`w-full rounded-lg border bg-white p-3 text-left transition motion-reduce:transition-none hover:bg-gray-50 ${
                            active ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${t.color}`} />
                            <span className={`text-xs font-medium ${active ? 'text-blue-700' : 'text-gray-500'}`}>
                                {t.label}
                            </span>
                        </div>
                        <p className="mt-1 text-xl font-bold tabular-nums text-gray-900">
                            {t.value === undefined ? '—' : fmtNum(t.value)}
                        </p>
                    </button>
                );
            })}
        </>
    );
}
